"use client";

import type { JSX } from "react";
import {
  type LayoutType,
  layoutList
} from "@/app/providers/layout/LayoutContext";
import { Button, Icon, Text, View } from "@/components";
import useDropdown from "@/hooks/useDropdown";
import { useLayout } from "@/providers";

/**
 * @name SelectLayout
 * @description A dropdown component that allows users to switch between different application layouts (e.g., Vertical, Horizontal).
 * It uses the `useLayout` hook to access and update the global layout state.
 *
 * @returns {JSX.Element} The rendered layout selector dropdown.
 */
const SelectLayout = (): JSX.Element => {
  const { layout, changeLayout } = useLayout();
  const { open, ref, toggleOpen } = useDropdown();

  return (
    <View ref={ref} className="relative hidden lg:block border rounded-[30px]">
      <Button
        action={toggleOpen}
        className="cursor-pointer w-full rounded-[30px] py-1 flex items-center justify-between gap-2 px-4 xxl:px-6"
      >
        <View>
          <Icon name="layers-down-right" />
          <Text className="flex items-center gap-2 select-none mr-4">
            {layout}
          </Text>
        </View>
        <Icon
          name="chevron-down"
          className={`shrink-0 cursor-pointer duration-300 ${open && "rotate-180"}`}
        />
      </Button>
      <View
        type="ul"
        className={`absolute top-full bg-white dark:bg-slate-900 border dark:border-n500 duration-300 origin-top shadow-md rounded-lg z-[21] left-0 p-2 w-full ${open ? "opacity-100 scale-100 visible" : "opacity-0 scale-0 invisible"}`}
      >
        {layoutList.map((item: LayoutType) => (
          <View
            type="li"
            key={item}
            className={`p-2 block select-none rounded-md cursor-pointer duration-300 ${layout === item ? "bg-primary text-n0 hover:!text-n0" : "hover:bg-chart-1"}`}
          >
            <Button
              action={() => {
                changeLayout(item);
                toggleOpen();
              }}
            >
              {item}
            </Button>
          </View>
        ))}
      </View>
    </View>
  );
};

export default SelectLayout;
